/**
 *
 * @module passThroughData
 *
 */

import Check from 'check-types';
import AddressableCreditCard from './addressableCreditCard';
import AmountedLineItem from './amountedLineItem';
import AppAccount from './appAccount';
import Currency from './currency';
import Locale from './locale';
import OrderCampaign from './orderCampaign';
import OrderSubmissionEnvironment from './orderSubmissionEnvironment';
import InvalidArgumentException from './../exception/invalidArgumentException';

const {
    array,
    instanceStrict,
    nonEmptyObject,
    nonEmptyString
} = Check.assert;

/**
 *
 * @class PassThroughData
 *
 */
export default class PassThroughData {

    /**
     *
     * @param {Object} options
     * @param {String} options.guid
     * @param {String} options.fingerPrint
     * @param {Boolean} [options.saveToWallet=false]
     * @param {AddressableCreditCard} options.addressableCreditCard
     * @param {Boolean} [options.anonymous=false]
     * @param {AppAccount} options.appAccount
     * @param {Currency} options.currency
     * @param {Array<AmountedLineItem>} options.lineItems
     * @param {Locale} options.locale
     * @param {Array<OrderCampaign>} options.orderCampaigns
     * @param {OrderSubmissionEnvironment} [options.orderSubmissionEnvironment=null]
     * @throws {InvalidArgumentException}
     *
     */
    constructor(options) {

        const errorMessage = {
            ctor: 'PassThroughData(options)',
            get options() {
                return `${this.ctor} options should be an Object`;
            },
            get guid() {
                return `${this.ctor} options.guid should be a String with length`;
            },
            get fingerPrint() {
                return `${this.ctor} options.fingerPrint should be a String with length`;
            },
            get addressableCreditCard() {
                return `${this.ctor} needs AddressableCreditCard instance`;
            },
            get appAccount() {
                return `${this.ctor} needs AppAccount instance`;
            },
            get currency() {
                return `${this.ctor} needs Currency instance`;
            },
            get lineItems() {
                return `${this.ctor} needs array of AmountedLineItem instances`;
            },
            get locale() {
                return `${this.ctor} needs Locale instance`;
            },
            get orderCampaigns() {
                return `${this.ctor} needs array of OrderCampaign instances`;
            }
        };

        nonEmptyObject(options, errorMessage.options, InvalidArgumentException);

        const { guid, fingerPrint, saveToWallet, addressableCreditCard, anonymous, appAccount, currency,
            lineItems, locale, orderCampaigns, orderSubmissionEnvironment } = options;

        nonEmptyString(guid, errorMessage.guid, InvalidArgumentException);
        nonEmptyString(fingerPrint, errorMessage.fingerPrint, InvalidArgumentException);
        instanceStrict(addressableCreditCard, AddressableCreditCard, errorMessage.addressableCreditCard, InvalidArgumentException); // eslint-disable-line
        instanceStrict(appAccount, AppAccount, errorMessage.appAccountError, InvalidArgumentException);
        instanceStrict(currency, Currency, errorMessage.currency, InvalidArgumentException);
        array.of.instanceStrict(lineItems, AmountedLineItem, errorMessage.lineItems, InvalidArgumentException);
        instanceStrict(locale, Locale, errorMessage.locale, InvalidArgumentException);
        array.of.instanceStrict(orderCampaigns, OrderCampaign, errorMessage.orderCampaigns, InvalidArgumentException);

        /**
         *
         * @access public
         * @type {String}
         *
         */
        this.guid = guid;

        /**
         *
         * @access public
         * @type {String}
         *
         */
        this.fingerPrint = fingerPrint;

        /**
         *
         * @access public
         * @type {Boolean}
         *
         */
        this.saveToWallet = Check.boolean(saveToWallet) ? saveToWallet : false;

        /**
         *
         * @access public
         * @type {SDK.Services.Commerce.AddressableCreditCard}
         *
         */
        this.addressableCreditCard = addressableCreditCard;

        /**
         *
         * @access public
         * @type {Boolean}
         *
         */
        this.anonymous = Check.boolean(anonymous) ? anonymous : false;

        /**
         *
         * @access public
         * @type {SDK.Services.Commerce.AppAccount}
         *
         */
        this.appAccount = appAccount;

        /**
         *
         * @access public
         * @type {String}
         *
         */
        this.billDate = new Date().toISOString();

        /**
         *
         * @access public
         * @type {SDK.Services.Commerce.Currency}
         *
         */
        this.currency = currency;

        /**
         *
         * @access public
         * @type {Array<SDK.Services.Commerce.AmountedLineItem>}
         *
         */
        this.lineItems = lineItems;

        /**
         *
         * @access public
         * @type {SDK.Services.Commerce.Locale}
         *
         */
        this.locale = locale;

        /**
         *
         * @access public
         * @type {Array<SDK.Services.Commerce.OrderCampaign>}
         *
         */
        this.orderCampaigns = orderCampaigns;

        /**
         *
         * @access public
         * @type {SDK.Services.Commerce.OrderSubmissionEnvironment}
         *
         */
        this.orderSubmissionEnvironment = Check.instanceStrict(orderSubmissionEnvironment, OrderSubmissionEnvironment)
            ? orderSubmissionEnvironment : null;

    }

}



// WEBPACK FOOTER //
// ./src/services/commerce/passThroughData.js